import { useEffect, useMemo } from 'react';

import { useNotificationCenterReadStatusLazyQuery } from 'generated/graphql-gateway';
import { actions, selectors, useAppDispatch, useAppSelector } from 'state/global-state';
import logger from 'utils/logger';

import { useNotificationCenterSession } from './use-notification-center-session';
import { getEligibleNotificationIds, setReadContentCardsInLocalStorage } from './utils';

/**
 * Fetches the server-confirmed read status for News feed Content Cards while authenticated, and
 * owns dispatching resetNotificationCenterSession on identity changes. Session cleanup runs
 * regardless of `enabled` (the experiment flag) so a flag flip doesn't strand stale state.
 */
export const useNotificationCenterReadStatus = ({ enabled }: { enabled: boolean }) => {
  const dispatch = useAppDispatch();
  const { sessionIdentity, isCurrent, sessionId } = useNotificationCenterSession();
  const newsFeedContentCards = useAppSelector(selectors.loyalty.selectNewsFeedContentCards);

  const notificationIds = useMemo(() => getEligibleNotificationIds(newsFeedContentCards), [
    newsFeedContentCards,
  ]);

  // network-only: read status is user-specific and must never be served from a stale/cross-user
  // cached entry.
  const [fetchReadStatus] = useNotificationCenterReadStatusLazyQuery({
    fetchPolicy: 'network-only',
  });

  // Dispatched on every identity change seen; the reducer no-ops if the owner is unchanged, and
  // survives this hook's own remount mid-transition. Runs even when disabled.
  useEffect(() => {
    dispatch(actions.loyalty.resetNotificationCenterSession(sessionIdentity));
  }, [sessionIdentity, dispatch]);

  // Gated on isCurrent (not just a resolved identity) to avoid firing under an ambiguous or
  // about-to-be-invalidated identity mid account-swap. sessionId is captured at request-start so
  // a response resolving after logout/relogin is ignored by the reducer instead of leaking in.
  useEffect(() => {
    if (!enabled || !isCurrent || notificationIds.length === 0) {
      return;
    }

    const requestSessionId = sessionId;

    fetchReadStatus({ variables: { notificationIds } })
      .then(({ data }) => {
        if (data) {
          const readNotificationIds = [...data.notificationCenterReadStatus.readNotificationIds];
          // Only applyServerReadStatus (not mergeServerReadNotificationIds) may mark the session resolved.
          dispatch(
            actions.loyalty.applyServerReadStatus({
              ids: readNotificationIds,
              requestedIds: notificationIds,
              sessionId: requestSessionId,
            })
          );
          // Also remember these on-device so they survive a later logout (see
          // setReadContentCardsInLocalStorage's doc).
          setReadContentCardsInLocalStorage(readNotificationIds);
        }
      })
      .catch(error => {
        logger.warn({ error, message: 'Failed to fetch notification center read status' });
      });
  }, [enabled, isCurrent, notificationIds, sessionId, fetchReadStatus, dispatch]);
};
